import { notification } from "antd";
import cuid from "cuid";
import { get } from "lodash";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { useSignUpAuth } from "shared/hooks/useSignUpAuth";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import { AUTH_PROVIDERS, SCREENS } from "utils/constants";
import { getAuthProvider } from "utils/helpers/authProvider";
import { run, useApiAccess, useEncryption } from "../Detail/hooks";
import { ADMIN_TYPES } from "../Detail/setup";
import {
  hasIntegrationChanges,
  INITIAL_VALUES,
  integrationsChanges,
  integrationsState,
  telecomsPayload,
} from "./onboarding";
import View from "./View";

const Create = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.ORGANIZATION);
  const ERRORS = t("ONBOARDING.ERRORS", { returnObjects: true });
  const DETAIL = t("DETAIL", { returnObjects: true });
  const navigate = useNavigate();
  const [userId, orgId] = useMemo(() => [cuid(), cuid()], []);
  const where = { id: orgId };
  const [submitted, setSubmitted] = useState(INITIAL_VALUES);
  const [created, setCreated] = useState(false);
  const [savingIntegrations, setSavingIntegrations] = useState(false);

  const integrationsQuery = useQuery(
    graphql.queries.ORGANIZATION_INTEGRATIONS,
    { variables: { where }, skip: !created, fetchPolicy: "network-only" },
  );
  const setupQuery = useQuery(graphql.queries.ORGANIZATION_SETUP, {
    variables: { where },
    skip: !created,
    fetchPolicy: "network-only",
  });
  const keys = useEncryption({
    where,
    skip: !created,
    labels: DETAIL.ENCRYPTION,
    onChanged: () => integrationsQuery.refetch(),
  });
  const api = useApiAccess({
    where,
    skip: !created,
    labels: DETAIL.API,
  });
  const catalog = get(integrationsQuery, "data.organizationIntegrations") || [];

  const [signUpAuth, { loading: authenticating }] = useSignUpAuth({
    onError: () => notification.error({ message: ERRORS.EMAIL_IN_USE }),
  });
  const [updateUser] = useMutation(graphql.mutations.UPDATE_USER_AUTH);
  const [onboardOrganization, { loading: onboarding }] = useMutation(
    graphql.mutations.ONBOARD_ORGANIZATION,
    { onCompleted: () => setCreated(true) },
  );
  const [setIntegrations] = useMutation(
    graphql.mutations.SET_ORGANIZATION_INTEGRATIONS,
  );

  const initialValues = created
    ? { ...submitted, integrations: integrationsState(catalog) }
    : INITIAL_VALUES;

  const rollback = () => {
    if (getAuthProvider() !== AUTH_PROVIDERS.ZITADEL)
      updateUser({
        variables: {
          where: { id: userId },
          data: { phone: null, username: null, email: null, password: null },
        },
      });
    notification.error({ message: ERRORS.FAILED });
  };

  const onSubmit = (values) => {
    const { organization, telecoms, icon, user } = values;
    const type = ADMIN_TYPES[organization.type];
    setSubmitted(values);
    signUpAuth({
      variables: {
        data: {
          id: userId,
          username: user.email,
          email: user.email,
          phone: user.phone,
          password: cuid(),
          meta: { type },
          organization: { id: orgId, name: organization.name },
        },
      },
      onCompleted: ({ signUp: authResult }) =>
        onboardOrganization({
          variables: {
            data: {
              organization: {
                ...organization,
                id: orgId,
                form: organization.form || null,
                siretNumber: organization.siretNumber || null,
                telecoms: telecomsPayload(telecoms, cuid),
                icon,
              },
              administrator: {
                ...user,
                id: userId,
                meta: { ...get(authResult, "meta", {}), type },
              },
              baseUrl: process.env.REACT_APP_BASE_URL,
            },
          },
          onError: rollback,
        }),
    });
  };

  const saveIntegrations = (values) => {
    const changes = integrationsChanges(values, initialValues, catalog);
    if (!hasIntegrationChanges(changes)) return Promise.resolve();
    setSavingIntegrations(true);
    return run(setIntegrations, { variables: { where, ...changes } })
      .then(() =>
        Promise.all([integrationsQuery.refetch(), setupQuery.refetch()]),
      )
      .catch((error) => {
        notification.error({ message: DETAIL.FAILED });
        throw error;
      })
      .finally(() => setSavingIntegrations(false));
  };

  const detailPath = `${SCREENS.PRIVATE.ADMIN.ORGANIZATIONS.DETAIL.path}/${orgId}`;

  return (
    <View
      initialValues={initialValues}
      created={created}
      catalog={catalog}
      setup={get(setupQuery, "data.organizationSetup") || {}}
      keys={keys}
      api={api}
      saving={authenticating || onboarding || savingIntegrations}
      onSubmit={onSubmit}
      onSaveIntegrations={saveIntegrations}
      onFinish={() => navigate(detailPath)}
      onClose={() =>
        navigate(
          created ? detailPath : SCREENS.PRIVATE.ADMIN.ORGANIZATIONS.LIST.path,
        )
      }
    />
  );
};

export default Create;
